// Platform adapter: the single boundary between the browser UI and the host.
//
// In the Electron build the preload script exposes `window.stenal` (see
// src/main/preload.ts); `platform` resolves to it, so the desktop app behaves
// exactly as before. In the web build a `webPlatform` implementation is
// injected instead (secrets go through the lightweight server; files/print use
// browser APIs). Every renderer call to the host goes through `platform`, so
// swapping the host is a one-line change here — no UI code needs to know which
// build it is running in.

import type {
  AppSettings,
  BenchSlatPreset,
  CadUser,
  Cartiglio,
  ChangelogEntry,
  DeletedProduct,
  Drawing,
  ImportedHeater,
  Lang,
  ListProjectsQuery,
  PipeType,
  ProductTemplate,
  ProductWriteResult,
  SavedColor,
  SavedProjectFull,
  SavedProjectPage,
  SavedProjectSummary,
  SaveProjectRequest,
  SteamBenchPreset,
} from '../shared/types';

/** The host API the renderer depends on (mirrors src/main/preload.ts). */
export interface StenalApi {
  openDrawing(): Promise<{ ok: boolean; fileName?: string; filePath?: string; dxfText?: string; pdfBase64?: string; imageDataUrl?: string; error?: string }>;
  importProductFile(): Promise<{ ok: boolean; fileName?: string; filePath?: string; dxfText?: string; imageDataUrl?: string; pdfBase64?: string; error?: string }>;
  /** Convert DWG bytes to DXF text — used by drag-and-drop, which has the file's
   *  bytes but no path (desktop: external converter via IPC; web: the server). */
  dwgToDxf(bytes: ArrayBuffer, fileName: string): Promise<string>;
  listProducts(): Promise<ProductTemplate[]>;
  /** Save one product, logged first; refused over a version other than `base`
   *  (its `hashProduct` when the edit began; null for a product not stored yet). */
  saveProduct(product: ProductTemplate, base: string | null): Promise<ProductWriteResult>;
  /** Delete one product, as `saveProduct` saves one. */
  deleteProduct(id: string, base: string | null): Promise<ProductWriteResult>;
  /** The products whose latest change-log entry is their deletion. */
  listDeletedProducts(): Promise<DeletedProduct[]>;
  productChangelog(productId: string): Promise<ChangelogEntry[]>;
  listColors(): Promise<SavedColor[]>;
  saveColor(color: SavedColor): Promise<SavedColor>;
  deleteColor(id: number): Promise<boolean>;
  listCartigli(): Promise<Cartiglio[]>;
  saveCartiglio(c: Cartiglio): Promise<Cartiglio>;
  deleteCartiglio(rowId: number): Promise<boolean>;
  listBenchPresets(): Promise<BenchSlatPreset[]>;
  saveBenchPreset(p: BenchSlatPreset): Promise<BenchSlatPreset>;
  /** Removes a slat or a steam preset alike (one table). */
  deleteBenchPreset(rowId: number): Promise<boolean>;
  listSteamBenchPresets(): Promise<SteamBenchPreset[]>;
  saveSteamBenchPreset(p: SteamBenchPreset): Promise<SteamBenchPreset>;
  /** The sculpted bench's sauna profiles: the same table, `kind: 'sauna-profile'`. */
  listSaunaProfiles(): Promise<SteamBenchPreset[]>;
  saveSaunaProfile(p: SteamBenchPreset): Promise<SteamBenchPreset>;
  /** The pipe types: the built-ins merged with the Piping table's rows. */
  listPipeTypes(): Promise<PipeType[]>;
  savePipeType(t: PipeType): Promise<PipeType>;
  deletePipeType(rowId: number): Promise<boolean>;
  translate(text: string, from: string, to: string): Promise<string>;
  getSettings(): Promise<AppSettings>;
  saveSettings(patch: Partial<AppSettings>): Promise<AppSettings>;
  /** Rebuild the desktop native menu in the given UI language (no-op on web). */
  setMenuLang?(lang: Lang): Promise<void>;
  /** Whether the signed-in user may edit shared standards (catalogue / benches /
   *  colours / title blocks). Absent (desktop) ⇒ always allowed. */
  canEditStandards?(): boolean;
  importHeater(url: string): Promise<{ ok: boolean; heater?: ImportedHeater; error?: string }>;
  /** Save the project: to its own file, or — `saveAs`, or never saved — where the
   *  user picks. Resolves to the project's name, or null when cancelled. */
  saveProject(drawing: Drawing, saveAs?: boolean): Promise<string | null>;
  /** Desktop: open a recent project file by path. */
  openProjectPath?(path: string): Promise<{ drawing: Drawing; name: string } | null>;
  /** Desktop: the drawing has no file any more (a new one): the next Save asks. */
  forgetProjectPath?(): Promise<void>;
  /** Desktop: File ▸ Open Recent picked a file. */
  onOpenRecent?(cb: (path: string) => void): void;
  openProject(): Promise<{ drawing: Drawing; name: string } | null>;
  /** Cloud (server-mediated) project storage. Web build only — absent on desktop
   *  (which is unauthenticated), so the cloud menu entries hide when unavailable. */
  listCloudProjects?(query?: ListProjectsQuery): Promise<SavedProjectPage>;
  loadCloudProject?(rowId: number): Promise<SavedProjectFull>;
  saveCloudProject?(payload: SaveProjectRequest): Promise<SavedProjectSummary>;
  deleteCloudProject?(rowId: number): Promise<void>;
  /** Rename a cloud project in the database (changes only its stored name). */
  renameCloudProject?(rowId: number, name: string): Promise<SavedProjectSummary>;
  /** The directory of users a project can be shared with (id + display name). */
  listCadUsers?(): Promise<CadUser[]>;
  /** Add user ids to a project's acl (share it with them). */
  shareCloudProject?(rowId: number, userIds: string[]): Promise<SavedProjectSummary>;
  /** Remove user ids from a project's acl (unshare). A member may remove only
   *  themselves; the owner or an `edit` user may remove anyone. */
  unshareCloudProject?(rowId: number, userIds: string[]): Promise<SavedProjectSummary>;
  /** The signed-in user's id (web build), for owner-vs-member UI decisions.
   *  Absent/empty on desktop (which is unauthenticated). */
  currentUserId?(): string;
  /** The signed-in user's display name (Bubble `nome`); web build only. */
  currentUserName?(): string;
  /** The signed-in user's area-manager id, '' when they have none. The server
   *  auto-adds it to every project they create, so it is access nobody granted
   *  by hand — the cloud gallery discounts it when deciding what reads as
   *  "shared". Web build only. */
  currentUserAreaManager?(): string;
  /** Whether the signed-in user has the `commerciale` claim (sees every user's
   *  cloud projects). Web build only; absent/false on desktop. */
  isCommerciale?(): boolean;
  /** Ask the user what to do about unsaved changes before an action that would
   *  discard them: 0 = Save, 1 = Don't Save, 2 = Cancel. */
  confirmUnsaved?(): Promise<number>;
  /** Desktop only: proceed with closing the window (the renderer has already
   *  handled any unsaved changes). Absent on web (which uses `beforeunload`). */
  confirmClose?(): Promise<void>;
  printImage(dataUrl: string, landscape: boolean, jobName: string, widthMm?: number, heightMm?: number): Promise<boolean>;
  printImages(pages: Array<{ dataUrl: string; widthMm: number; heightMm: number }>, jobName: string): Promise<boolean>;
  exportSheetPdf(dataUrl: string, widthMm: number, heightMm: number, landscape: boolean, defaultName: string): Promise<string | null>;
  exportAllPdf(pages: Array<{ dataUrl: string; widthMm: number; heightMm: number }>, defaultName: string): Promise<string | null>;
  exportDxf(dxf: string, defaultName: string): Promise<string | null>;
  askDxfInclude(): Promise<number>;
  exportModel3d(data: Uint8Array | string, defaultName: string, ext: 'glb' | 'obj' | 'dxf' | 'stl' | 'usdz', mtl?: string): Promise<string | null>;
  onMenu(channel: string, cb: () => void): void;
  onBaserowError(cb: (message: string) => void): void;
  onBaserowBusy(cb: (busy: boolean) => void): void;
  notifyReady(): void;
}

declare global {
  interface Window {
    /** Electron preload bridge (absent in the web build). */
    stenal?: StenalApi;
    /** Web build's host implementation, injected before the renderer runs. */
    stenalWeb?: StenalApi;
  }
}

/** The active host: Electron's preload bridge, else the injected web adapter.
 *  Exactly one is present at runtime. */
export const platform: StenalApi = (window.stenal ?? window.stenalWeb)!;
